import React, { useCallback } from "react";
import styles from "./Controls.module.css";
import {
  Room as TwilioRoom,
  LocalVideoTrack,
} from "twilio-video";
import { useDispatch, useSelector } from "react-redux";
import { removeToken, setLobbyStep } from "store/user/slice";
import {
  toggleVideo,
  toggleAudio,
  toggleScreenShare,
  toggleLocalTrackControls,
  setLocalVideoTrackInfo,
} from "store/local-tracks/slice";
import { RootState } from "store/reducers";
import { uuidv4, getPublicationTracks } from "utils";
import { isMobile } from "react-device-detect";
import { Icon } from "components/shared/icon/Icon";
import { AudioLevelMeter } from "components/shared/preview/AudioLevelMeter";
import { resetRoom } from "store/room/thunks";
import { LobbyStep } from "store/user/types";
import { toggleChat } from "store/chat-messages/slice";

interface ControlsProps {
  room?: TwilioRoom;
}
export const Controls: React.FC<ControlsProps> = ({ room }) => {
  const dispatch = useDispatch();
  const {
    busy,
    localAudioTrack,
    localVideoTrack,
    muteAudio,
    hideVideo,
    shareScreen,
    showLocalTrackControls,
  } = useSelector((s: RootState) => s.localTracks);
  const mediaDevices = useSelector((s: RootState) => s.mediaDevices);
  const { participants } = useSelector((s: RootState) => s.remoteParticipants);
  const { isInteracting, privateChatEnabled } = useSelector((s: RootState) => s.user);
  const onLeave = useCallback(() => {
    dispatch(removeToken());
    dispatch(resetRoom());
    dispatch(setLobbyStep(LobbyStep.EnterName));
    window.location.reload();

  }, [dispatch]);
  const { showChat, unreadCount } = useSelector(
    (s: RootState) => s.chatMessages
  );

  /** Create and publish a new screen share track. */
  const share = useCallback(async () => {
    if (!shareScreen) {
      const stream = await (navigator.mediaDevices as any).getDisplayMedia({
        video: {
          height: 1080,
          width: 1920,
          frameRate: 15,
        },
      });

      if (stream) {
        const selected = stream.getTracks()[0];
        const screenTrack = new LocalVideoTrack(selected, {
          name: `${uuidv4()}-screen`,
          logLevel: "error",
        });

        await room?.localParticipant.publishTrack(
          screenTrack as any,
          { priority: "high" } as any
        );
        dispatch(toggleScreenShare(true));

        screenTrack.on("stopped", async () => {
          room?.localParticipant.unpublishTrack(screenTrack);
          dispatch(toggleScreenShare(false));
        });
      }
    } else {
      if (room?.localParticipant) {
        const screenShareTrack = getPublicationTracks(
          room?.localParticipant.videoTracks
        ).find((t) => /-screen/g.test(t.name));
        if (screenShareTrack) {
          screenShareTrack.stop();
          dispatch(toggleScreenShare(false));
        }
      }
    }
  }, [dispatch, room, shareScreen]);

  const swap = useCallback(() => {
    if (localVideoTrack) {
      if (
        /back/.test(
          localVideoTrack?.mediaStreamTrack?.label.toLowerCase() || ""
        )
      ) {
        const track = mediaDevices.videoInputs.find((vi) =>
          /front/.test(vi.label.toLowerCase())
        );

        if (track) {
          dispatch(
            setLocalVideoTrackInfo({
              deviceId: track.deviceId,
              label: track.label,
            })
          );
        }
      } else if (
        /front/.test(
          localVideoTrack?.mediaStreamTrack?.label.toLowerCase() || ""
        )
      ) {
        const track = mediaDevices.videoInputs.find((vi) =>
          /back/.test(vi.label.toLowerCase())
        );

        if (track) {
          dispatch(
            setLocalVideoTrackInfo({
              deviceId: track.deviceId,
              label: track.label,
            })
          );
        }
      }
    }
  }, [dispatch, localVideoTrack, mediaDevices]);

  const chatToggle = useCallback(() => {
    dispatch(toggleChat(!showChat));
  }, [dispatch, showChat]);

  return (
    <>
      <div
        className={`${styles["controls-container"]} ${
          isInteracting ? styles["show"] : ""
        }`}
      >
        <div className={`${styles["left"]}`}>
          <button
            type="button"
            onClick={() =>
              dispatch(toggleLocalTrackControls(!showLocalTrackControls))
            }
          >
            <Icon id={"gear"} />
          </button>
        </div>
        <div className={styles["middle"]}>
          {isMobile && (
            <>
              {privateChatEnabled && <button
                type="button"
                onClick={chatToggle}
              >
                <Icon id={"chat"} />
                {unreadCount > 0 && (
                  <span className={`${styles["badge"]}`}>
                    {unreadCount}
                  </span>
                )}
              </button>}
              <button
                type="button"
                onClick={swap}
                disabled={busy.video || hideVideo}
              >
                <Icon id={"flip-camera"} />
              </button>
            </>
          )}
          <button
            type="button"
            className={`${hideVideo ? styles["disabled"] : ""}`}
            onClick={() => dispatch(toggleVideo(!hideVideo))}
            disabled={busy.video}
          >
            <Icon id={"video-camera"} />
            <span className={styles["slash"]}>
              <Icon id={"slash"} />
            </span>
          </button>
          <button
            type="button"
            className={muteAudio ? styles["disabled"] : ""}
            onClick={() => dispatch(toggleAudio(!muteAudio))}
          >
            {localAudioTrack && !muteAudio && (
              <AudioLevelMeter audioTrack={localAudioTrack} />
            )}
            <Icon id={"microphone"} />
            <span className={styles["slash"]}>
              <Icon id={"slash"} />
            </span>
          </button>
          {!isMobile && (
            <button
              type="button"
              className={
                participants.some((p) => p.isSharingScreen)
                  ? styles["disabled"]
                  : ""
              }
              onClick={() => share()}
              disabled={participants.some((p) => p.isSharingScreen)}
              title={
                participants.some((p) => p.isSharingScreen)
                  ? "Someone else is sharing their screen right now!"
                  : ""
              }
            >
              <Icon id={"present"} />
              <span className={styles["slash"]}>
                <Icon id={"slash"} />
              </span>
            </button>
          )}
        </div>
        <div className={styles["right"]}>
          <button className={styles["hangup"]} onClick={onLeave}>
            <Icon id={"close"} />
          </button>
        </div>
      </div>
    </>
  );
};
